import { SUPPLEMENTAL_DATA_SYNC_CHANGE } from '../../reducers/supplementalDataSyncReducer';
import { type TlbSignedUserState } from '../../reducers/tlbUserReducer';
import { onEverySyncEnd } from '../../utils/useOnSync';
import { store } from '../../wrappers/WithStore';
import { type LocalDB } from '../LocalDB/LocalDB';
import { getDb } from '../LocalDB/LocalDB';
import { updateSupplementalData } from './FlightSupplementalDataSource';

const LOCAL_STORAGE_KEY_SUPPLEMENTAL_LAST_SYNC = 'alol_supplemental_synctime';
// Matches AppSync's DeltaSync table retention (see DbSyncer.shouldDoCompleteResync) - past this,
// a delta sync would be no cheaper than a full one anyway, so fall back to the scoped full query.
const FULL_RESYNC_THRESHOLD_MS = 30 * 60000;

export class FlightSupplementalDataManager {
    private readonly db: LocalDB;
    user: TlbSignedUserState;
    private nowLoading = false;
    private ended = false;
    private loadingPromise: Promise<void> | null = null;
    private rerunPromise: Promise<void> | null = null;
    private readonly onEverySyncSubscription: ReturnType<typeof onEverySyncEnd>;
    private lastSyncTime: number = Number.parseInt(
        globalThis.localStorage.getItem(LOCAL_STORAGE_KEY_SUPPLEMENTAL_LAST_SYNC) || '0',
        10
    );

    constructor(db: LocalDB, user: TlbSignedUserState) {
        this.db = db;
        this.user = user;
        this.onEverySyncSubscription = onEverySyncEnd(() => {
            this.updateData();
        });
    }

    async updateData(): Promise<void> {
        if (this.ended) {
            return;
        }
        if (this.nowLoading && this.loadingPromise) {
            // the running load may have started before the caller's change - queue (at most) one fresh load after it
            this.rerunPromise ??= this.loadingPromise.then(async () => {
                this.rerunPromise = null;

                return this.updateData();
            });

            return this.rerunPromise;
        }
        this.nowLoading = true;
        this.dispatchSupplementalDataSyncChange(true);
        this.loadingPromise = this.loadData()
            .catch((error: unknown) => console.error('Supplemental data load failed', error))
            .finally(() => {
                this.nowLoading = false;
                this.dispatchSupplementalDataSyncChange(false);
            });

        return this.loadingPromise;
    }

    end() {
        this.ended = true;
        this.onEverySyncSubscription.end();
    }

    private async shouldDoCompleteResync(): Promise<boolean> {
        if (this.lastSyncTime === 0 || Date.now() - this.lastSyncTime > FULL_RESYNC_THRESHOLD_MS) {
            return true;
        }

        // defensive: local content missing (e.g. the local DB was wiped) even though the cursor looks fresh
        return (await this.db.flightSupplementalData.count()) === 0;
    }

    private setLastSyncTime(time: number) {
        this.lastSyncTime = time;
        globalThis.localStorage.setItem(LOCAL_STORAGE_KEY_SUPPLEMENTAL_LAST_SYNC, time.toString());
    }

    private async loadData() {
        const cycleStart = Date.now();
        const doFullResync = await this.shouldDoCompleteResync();
        const ok = await updateSupplementalData(this.db, this.user, {
            doFullResync,
            lastSync: this.lastSyncTime,
        });
        // a manager ended mid-load (sign-out) must not put a fresh cursor back
        if (ok && !this.ended) {
            this.setLastSyncTime(cycleStart);
        }
    }

    private dispatchSupplementalDataSyncChange(newInProgressState: boolean) {
        store.dispatch({
            type: SUPPLEMENTAL_DATA_SYNC_CHANGE,
            data: {
                inProgress: newInProgressState,
                time: Date.now(),
            },
        });
    }
}

let instance: FlightSupplementalDataManager | null = null;
let currentUser: TlbSignedUserState | null = null;

// reauth dispatches a new (but equal) user object - keep the running manager then
function isSameIdentity(a: TlbSignedUserState, b: TlbSignedUserState): boolean {
    return (
        a.data.userId === b.data.userId &&
        a.data.contactId === b.data.contactId &&
        a.data.crewId === b.data.crewId &&
        a.data.aircraftId === b.data.aircraftId
    );
}

export function getFlightSupplementalDataManager(user: TlbSignedUserState): FlightSupplementalDataManager {
    if (instance && currentUser && isSameIdentity(user, currentUser)) {
        instance.user = user;
    } else {
        if (instance) {
            instance.end();
        }
        instance = new FlightSupplementalDataManager(getDb(), user);
        currentUser = user;
    }

    return instance;
}

// sign-out: the next user must not inherit this user's manager nor its sync cursor
export function wipeFlightSupplementalDataManager(): void {
    instance?.end();
    instance = null;
    currentUser = null;
    globalThis.localStorage.removeItem(LOCAL_STORAGE_KEY_SUPPLEMENTAL_LAST_SYNC);
}
